import React, { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useInstructorSplits } from '@/hooks/useInstructorSplits'
import { useUsers } from '@/hooks/useUsers'
import { instructorSplitService } from '@/services/instructorSplitService'

interface ModuleInstructorCardProps {
    moduleId: string
    courseId: string
}

export const ModuleInstructorCard: React.FC<ModuleInstructorCardProps> = ({
    moduleId,
    courseId,
}) => {
    const [showAssignModal, setShowAssignModal] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)
    const [selectedInstructorId, setSelectedInstructorId] = useState<string | null>(null)

    const queryClient = useQueryClient()
    const { data: splits, isLoading: isLoadingSplits } = useInstructorSplits(courseId)
    const { data: allInstructors, isLoading: isLoadingInstructors } = useUsers({ role: 'instructor' })

    // NULL/empty moduleAssignments = instructor has access to all modules in the course
    const isAssignedToModule = (split: { moduleAssignments?: string[] | null }, moduleId: string) => {
        const assignments = split.moduleAssignments
        if (!assignments || assignments.length === 0) return true
        return assignments.includes(moduleId)
    }

    // Find instructors assigned to this module
    const assignedInstructors = useMemo(() => {
        if (!splits || !allInstructors) return []

        return splits
            .filter(split => isAssignedToModule(split, moduleId))
            .map(split => {
                const instructor = allInstructors.find(i => i.id === split.instructorId)
                return {
                    splitId: split.id,
                    instructorId: split.instructorId,
                    name: instructor?.name || 'Unknown',
                    email: instructor?.email || '',
                }
            })
    }, [splits, allInstructors, moduleId])

    // Get available instructors that are assigned to the course but not to this module
    const availableInstructors = useMemo(() => {
        if (!splits || !allInstructors) return []

        return splits
            .filter(split => !isAssignedToModule(split, moduleId))
            .map(split => {
                const instructor = allInstructors.find(i => i.id === split.instructorId)
                return {
                    splitId: split.id,
                    instructorId: split.instructorId,
                    name: instructor?.name || 'Unknown',
                    email: instructor?.email || '',
                    currentModules: split.moduleAssignments || [],
                }
            })
    }, [splits, allInstructors, moduleId])

    const handleAssignInstructor = async () => {
        if (!selectedInstructorId) return

        const split = splits?.find(s => s.instructorId === selectedInstructorId)
        if (!split) return

        setIsUpdating(true)
        try {
            const updatedModules = [...(split.moduleAssignments || []), moduleId]
            await instructorSplitService.updateSplit(courseId, selectedInstructorId, {
                moduleAssignments: updatedModules,
            })
            queryClient.invalidateQueries({ queryKey: ['instructor-splits', courseId] })
            setShowAssignModal(false)
            setSelectedInstructorId(null)
        } catch (error) {
            console.error('Failed to assign instructor:', error)
        } finally {
            setIsUpdating(false)
        }
    }

    const handleRemoveInstructor = async (instructorId: string) => {
        if (!confirm('Remove this instructor from this module?')) return

        const split = splits?.find(s => s.instructorId === instructorId)
        if (!split) return

        setIsUpdating(true)
        try {
            const updatedModules = (split.moduleAssignments || []).filter(m => m !== moduleId)
            await instructorSplitService.updateSplit(courseId, instructorId, {
                moduleAssignments: updatedModules,
            })
            queryClient.invalidateQueries({ queryKey: ['instructor-splits', courseId] })
        } catch (error) {
            console.error('Failed to remove instructor:', error)
        } finally {
            setIsUpdating(false)
        }
    }

    if (isLoadingSplits || isLoadingInstructors) {
        return (
            <div className="flex items-center gap-2 text-gray-500 dark:text-text-muted text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                Loading instructors...
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary text-lg">person</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Assigned Instructors</span>
                </div>
                {availableInstructors.length > 0 && (
                    <button
                        onClick={() => setShowAssignModal(true)}
                        disabled={isUpdating}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary hover:text-primary-hover transition-colors disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined text-sm">add</span>
                        Add
                    </button>
                )}
            </div>

            {/* Assigned Instructors List */}
            {assignedInstructors.length > 0 ? (
                <div className="space-y-2">
                    {assignedInstructors.map(instructor => (
                        <div
                            key={instructor.instructorId}
                            className="flex items-center justify-between p-2 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        >
                            <div className="flex items-center gap-2">
                                <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center">
                                    <span className="text-xs font-bold text-primary">
                                        {instructor.name.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{instructor.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-text-muted">{instructor.email}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleRemoveInstructor(instructor.instructorId)}
                                disabled={isUpdating}
                                className="p-1 rounded hover:bg-red-500/20 text-gray-500 dark:text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                                title="Remove from module"
                            >
                                <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-gray-500 dark:text-text-muted italic">No instructors assigned to this module</p>
            )}

            {/* Assign Modal */}
            {showAssignModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80" onClick={() => setShowAssignModal(false)} />
                    <div className="relative w-full max-w-sm rounded-lg bg-slate-900 border border-gray-200 dark:border-white/10 p-4">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4">Assign Instructor to Module</h3>

                        {availableInstructors.length > 0 ? (
                            <>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {availableInstructors.map(instructor => (
                                        <label
                                            key={instructor.instructorId}
                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${selectedInstructorId === instructor.instructorId
                                                ? 'bg-primary/20 border border-primary'
                                                : 'bg-gray-100 dark:bg-white/5 border border-transparent hover:bg-gray-200 dark:hover:bg-white/10'
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="instructor"
                                                value={instructor.instructorId}
                                                checked={selectedInstructorId === instructor.instructorId}
                                                onChange={() => setSelectedInstructorId(instructor.instructorId)}
                                                className="sr-only"
                                            />
                                            <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center">
                                                <span className="text-xs font-bold text-primary">
                                                    {instructor.name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">{instructor.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-text-muted">{instructor.email}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                                    <button
                                        onClick={() => setShowAssignModal(false)}
                                        className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAssignInstructor}
                                        disabled={!selectedInstructorId || isUpdating}
                                        className="px-3 py-1.5 text-sm font-bold bg-primary text-white rounded-lg disabled:opacity-50"
                                    >
                                        {isUpdating ? 'Assigning...' : 'Assign'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-text-muted">
                                All course instructors are already assigned to this module.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ModuleInstructorCard
